import { useEffect, useMemo, useState } from 'react';
import { useFetchReportsSummaryQuery } from '../../api';
import { formatAmount } from '../../utils';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { FormControl, MenuItem, Select, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

interface WarningMessage {
  id: string;
  message: string;
  timestamp: Date;
  type: 'deletion' | 'warning';
  itemName: string;
  gitlabUsername?: string;
  deletedData?: {
    soldQuantity: number;
    givenQuantity: number;
    totalStock: number;
    soldMoney: number;
    stockRemaining: number;
  };
}

type CategoryKey = 'Snacks' | 'Drinks' | 'Other';
type CategoryFilter = 'all' | CategoryKey;

const LOW_STOCK_THRESHOLD = 10;

const safeNumber = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeType = (catType?: string, catName?: string): CategoryKey => {
  const t = (catType || '').trim().toLowerCase();
  const n = (catName || '').trim().toLowerCase();
  if (t === 'snacks' || n === 'snacks' || n === 'sweet' || n === 'biscuit' || n === 'chocolate' || n === 'cake') return 'Snacks';
  if (t === 'drinks' || n === 'drinks' || n === 'water' || n === 'juice' || n === 'fanta' || n === 'milk' || n === 'energy') return 'Drinks';
  return 'Other';
};

const emptyBucket = () => ({
  soldQuantity: 0,
  givenQuantity: 0,
  remainingQuantity: 0,
  revenue: 0,
  giveawayValue: 0,
  totalProducts: 0,
});

const MenuReport = () => {
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [allTime, setAllTime] = useState(false);
  const [filterCategory, setFilterCategory] = useState<CategoryFilter>('all');
  const [warnings, setWarnings] = useState<WarningMessage[]>([]);

  useEffect(() => {
    try {
      const storedWarnings = localStorage.getItem('menuWarnings');
      if (!storedWarnings) return;
      const parsedWarnings: WarningMessage[] = JSON.parse(storedWarnings);
      const validWarnings = parsedWarnings.filter((warning) => {
        const hoursDiff = (Date.now() - new Date(warning.timestamp).getTime()) / (1000 * 60 * 60);
        return hoursDiff < 24;
      });
      setWarnings(validWarnings);
      localStorage.setItem('menuWarnings', JSON.stringify(validWarnings));
    } catch {
      setWarnings([]);
    }
  }, []);

  const params = useMemo(() => {
    if (allTime) return {};
    const from = (startDate ?? dayjs().startOf('month')).startOf('day');
    const to = (endDate ?? dayjs()).endOf('day');
    return { from: from.toISOString(), to: to.toISOString() };
  }, [allTime, startDate, endDate]);

  const { data: summary, isLoading, isFetching } = useFetchReportsSummaryQuery(params);

  const itemDetails: any[] = useMemo(
    () => (Array.isArray(summary?.allItemDetails) ? summary.allItemDetails : []),
    [summary]
  );

  // Roll the backend's category rows up into the three groups the report shows.
  const aggregates = useMemo(() => {
    const results: Record<CategoryKey, ReturnType<typeof emptyBucket>> = {
      Snacks: emptyBucket(),
      Drinks: emptyBucket(),
      Other: emptyBucket(),
    };
    const rows: any[] = Array.isArray(summary?.categorySummary) ? summary.categorySummary : [];
    rows.forEach((c) => {
      const bucket = results[normalizeType(c.categoryType, c.category)];
      bucket.soldQuantity += safeNumber(c.soldQuantity);
      bucket.givenQuantity += safeNumber(c.givenQuantity);
      bucket.remainingQuantity += safeNumber(c.remainingQuantity ?? c.stockQuantity);
      bucket.revenue += safeNumber(c.revenue ?? c.salesRevenue);
      bucket.giveawayValue += safeNumber(c.giveawayValue ?? c.givenValue);
      bucket.totalProducts += safeNumber(c.totalProducts);
    });
    return results;
  }, [summary]);

  const categoryRows = (
    [
      { key: 'Snacks' as CategoryKey, label: 'Snacks', ...aggregates.Snacks },
      { key: 'Drinks' as CategoryKey, label: 'Drinks', ...aggregates.Drinks },
      { key: 'Other' as CategoryKey, label: 'Others', ...aggregates.Other },
    ]
  ).filter((row) => filterCategory === 'all' || filterCategory === row.key);

  const totals = categoryRows.reduce(
    (acc, row) => ({
      soldQuantity: acc.soldQuantity + row.soldQuantity,
      givenQuantity: acc.givenQuantity + row.givenQuantity,
      remainingQuantity: acc.remainingQuantity + row.remainingQuantity,
      revenue: acc.revenue + row.revenue,
      giveawayValue: acc.giveawayValue + row.giveawayValue,
      totalProducts: acc.totalProducts + row.totalProducts,
    }),
    emptyBucket()
  );

  const visibleItems = itemDetails
    .filter((item) => filterCategory === 'all' || normalizeType(item.categoryType, item.category) === filterCategory)
    .slice()
    .sort((a, b) => safeNumber(b.revenue) - safeNumber(a.revenue));

  const lowStockCount = visibleItems.filter((item) => safeNumber(item.remainingQuantity) <= LOW_STOCK_THRESHOLD).length;

  const salesEntries: [string, number][] = categoryRows.map((row) => [row.label, row.revenue]);
  const giveawayEntries: [string, number][] = categoryRows.map((row) => [row.label, row.giveawayValue]);

  const periodLabel = allTime
    ? 'All time'
    : `${(startDate ?? dayjs().startOf('month')).format('MMM D')} - ${(endDate ?? dayjs()).format('MMM D, YYYY')}`;

  return (
    <div className="space-y-6">
      <div className="bg-white p-4 md:p-5 rounded-2xl shadow-sm border border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <Typography variant="h6" className="font-bold text-slate-800">Menu Report</Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <div className="flex flex-wrap items-center gap-3">
            <FormControl size="small" style={{ minWidth: 150 }}>
              <Select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value as CategoryFilter)} displayEmpty>
                <MenuItem value="all">All Categories</MenuItem>
                <MenuItem value="Snacks">Snacks</MenuItem>
                <MenuItem value="Drinks">Drinks</MenuItem>
                <MenuItem value="Other">Others</MenuItem>
              </Select>
            </FormControl>
            <DatePicker label="Start Date" value={startDate} onChange={setStartDate} disabled={allTime} slotProps={{ textField: { size: 'small' } }} />
            <DatePicker label="End Date" value={endDate} onChange={setEndDate} disabled={allTime} slotProps={{ textField: { size: 'small' } }} />
            <button
              type="button"
              onClick={() => setAllTime((v) => !v)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-colors ${
                allTime
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
            >
              All time
            </button>
          </div>
        </LocalizationProvider>
      </div>

      {warnings.length > 0 && (
        <div className="bg-white border border-rose-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="bg-rose-50 border-b border-rose-200 px-5 py-3 flex items-center gap-2">
            <WarningAmberIcon fontSize="small" className="text-rose-600" />
            <h3 className="font-bold text-rose-800 text-sm">Recent data clearances ({warnings.length})</h3>
            <span className="text-xs text-rose-500 ml-auto">Shown for 24 hours</span>
          </div>
          <div className="divide-y divide-slate-100">
            {warnings.map((warning) => (
              <div key={warning.id} className="px-5 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 text-sm">
                <div>
                  <span className="font-semibold text-slate-800">{warning.itemName}</span>
                  <span className="text-slate-600"> — {warning.message}</span>
                </div>
                <span className="text-xs text-slate-400">{new Date(warning.timestamp).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {(isLoading || isFetching) ? (
        <div className="flex items-center justify-center min-h-[200px] text-gray-500 italic">Loading menu report…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <SummaryTile label="Sales Revenue" value={`RWF ${formatAmount(totals.revenue)}`} accent="emerald" />
            <SummaryTile label="Giveaway Value" value={`RWF ${formatAmount(totals.giveawayValue)}`} accent="amber" />
            <SummaryTile label="Items Sold" value={totals.soldQuantity.toLocaleString()} />
            <SummaryTile label="Items Given" value={totals.givenQuantity.toLocaleString()} />
            <SummaryTile label="Remaining Stock" value={totals.remainingQuantity.toLocaleString()} />
            <SummaryTile label="Low Stock Items" value={lowStockCount.toLocaleString()} accent={lowStockCount > 0 ? 'rose' : undefined} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <BreakdownCard title="Sales by Category" entries={salesEntries} total={totals.revenue} />
            <BreakdownCard title="Giveaways by Category" entries={giveawayEntries} total={totals.giveawayValue} />
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="bg-slate-50 border-b border-slate-200 px-5 py-3 flex items-center justify-between">
              <h3 className="font-bold text-slate-800 text-sm">Category Breakdown</h3>
              <span className="text-xs text-slate-500">{periodLabel}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="py-3 px-4 text-left font-bold text-slate-700">Category</th>
                    <th className="py-3 px-4 text-right font-bold text-slate-700">Products</th>
                    <th className="py-3 px-4 text-right font-bold text-slate-700">Sold</th>
                    <th className="py-3 px-4 text-right font-bold text-slate-700">Given</th>
                    <th className="py-3 px-4 text-right font-bold text-slate-700">Remaining</th>
                    <th className="py-3 px-4 text-right font-bold text-slate-700">Sales Revenue</th>
                    <th className="py-3 px-4 text-right font-bold text-slate-700">Giveaway Value</th>
                  </tr>
                </thead>
                <tbody>
                  {categoryRows.map((row) => (
                    <tr key={row.key} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="py-3 px-4 text-slate-800 font-medium">{row.label}</td>
                      <td className="py-3 px-4 text-right text-slate-700">{row.totalProducts.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right text-slate-700">{row.soldQuantity.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right text-amber-600">{row.givenQuantity.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right text-slate-700">{row.remainingQuantity.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right font-semibold text-emerald-600">RWF {formatAmount(row.revenue)}</td>
                      <td className="py-3 px-4 text-right text-rose-600">RWF {formatAmount(row.giveawayValue)}</td>
                    </tr>
                  ))}
                  <tr className="bg-slate-50 font-bold border-t border-slate-200">
                    <td className="py-3 px-4 text-slate-900">Total</td>
                    <td className="py-3 px-4 text-right text-slate-900">{totals.totalProducts.toLocaleString()}</td>
                    <td className="py-3 px-4 text-right text-slate-900">{totals.soldQuantity.toLocaleString()}</td>
                    <td className="py-3 px-4 text-right text-amber-700">{totals.givenQuantity.toLocaleString()}</td>
                    <td className="py-3 px-4 text-right text-slate-900">{totals.remainingQuantity.toLocaleString()}</td>
                    <td className="py-3 px-4 text-right text-emerald-700">RWF {formatAmount(totals.revenue)}</td>
                    <td className="py-3 px-4 text-right text-rose-700">RWF {formatAmount(totals.giveawayValue)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="bg-slate-50 border-b border-slate-200 px-5 py-3 flex items-center justify-between">
              <h3 className="font-bold text-slate-800 text-sm">Items ({visibleItems.length})</h3>
              <span className="text-xs text-slate-500">Sold and given are for the selected period. Stock is current.</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="py-3 px-4 text-left font-bold text-slate-700">Item</th>
                    <th className="py-3 px-4 text-left font-bold text-slate-700">Category</th>
                    <th className="py-3 px-4 text-right font-bold text-slate-700">Unit Price</th>
                    <th className="py-3 px-4 text-right font-bold text-slate-700">Sold</th>
                    <th className="py-3 px-4 text-right font-bold text-slate-700">Given</th>
                    <th className="py-3 px-4 text-right font-bold text-slate-700">Remaining</th>
                    <th className="py-3 px-4 text-right font-bold text-slate-700">Revenue</th>
                    <th className="py-3 px-4 text-right font-bold text-slate-700">Giveaway Value</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleItems.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-8 text-center text-slate-400">No menu items in this category</td>
                    </tr>
                  ) : visibleItems.map((item, index) => {
                    const remaining = safeNumber(item.remainingQuantity);
                    const isLow = remaining <= LOW_STOCK_THRESHOLD;
                    return (
                      <tr key={`${item.name}-${index}`} className="border-b border-slate-100 hover:bg-slate-50/50">
                        <td className="py-3 px-4 text-slate-800 font-medium">{item.name}</td>
                        <td className="py-3 px-4 text-slate-700">{item.category || 'Uncategorized'}</td>
                        <td className="py-3 px-4 text-right text-slate-700">RWF {formatAmount(safeNumber(item.price))}</td>
                        <td className="py-3 px-4 text-right text-slate-700">{safeNumber(item.soldQuantity).toLocaleString()}</td>
                        <td className="py-3 px-4 text-right text-amber-600">{safeNumber(item.givenQuantity).toLocaleString()}</td>
                        <td className="py-3 px-4 text-right">
                          <span className={isLow ? 'inline-block px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 font-semibold text-xs' : 'text-slate-700'}>
                            {remaining.toLocaleString()}{isLow ? ' low' : ''}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right font-semibold text-emerald-600">RWF {formatAmount(safeNumber(item.revenue))}</td>
                        <td className="py-3 px-4 text-right text-rose-600">RWF {formatAmount(safeNumber(item.giveawayValue))}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const SummaryTile = ({ label, value, accent }: { label: string; value: string; accent?: 'emerald' | 'amber' | 'rose' }) => {
  const color = accent === 'emerald' ? 'text-emerald-600' : accent === 'amber' ? 'text-amber-600' : accent === 'rose' ? 'text-rose-600' : 'text-slate-800';
  return (
    <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">{label}</p>
      <p className={`text-xl font-bold ${color}`}>{value}</p>
    </div>
  );
};

const BreakdownCard = ({ title, entries, total }: { title: string; entries: [string, number][]; total: number }) => (
  <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
    <h3 className="font-bold text-slate-800 text-sm mb-4">{title}</h3>
    {entries.length === 0 || total <= 0 ? (
      <p className="text-slate-400 text-sm">No data</p>
    ) : (
      <div className="space-y-3">
        {entries.map(([key, value]) => {
          const pct = total > 0 ? (value / total) * 100 : 0;
          return (
            <div key={key}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-slate-700">{key}</span>
                <span className="text-sm font-bold text-slate-900">RWF {formatAmount(value)}</span>
              </div>
              <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-slate-800 rounded-full" style={{ width: `${pct}%` }} />
              </div>
              <div className="text-[10px] text-slate-400 mt-1">{pct.toFixed(1)}%</div>
            </div>
          );
        })}
      </div>
    )}
  </div>
);

export default MenuReport;

export const addMenuWarning = (itemName: string, deletedData?: WarningMessage['deletedData']) => {
  const getGitlabUsername = () => process.env.REACT_APP_GITLAB_USERNAME || 'Unknown User';
  const newWarning: WarningMessage = {
    id: Date.now().toString(),
    message: `All transaction data for "${itemName}" has been cleared by admin.`,
    timestamp: new Date(),
    type: 'deletion',
    itemName,
    gitlabUsername: getGitlabUsername(),
    deletedData,
  };
  try {
    const existingWarnings = JSON.parse(localStorage.getItem('menuWarnings') || '[]');
    localStorage.setItem('menuWarnings', JSON.stringify([...existingWarnings, newWarning]));
  } catch {
    // Storage unavailable; the warning is informational only.
  }
};
